import classNames from "classnames";
import { useMemo, useRef } from "react";
import { AccountingReportType } from "@common/types/reports/report-type";
import { interpolateReportVariables } from "@common/helpers/report-variables";
import { shouldShowRoundingDisclosure } from "@common/helpers/excel-presentation";
import {
    ROUNDING_FOOTNOTE_TEXT,
    buildScaleDisclosureText,
} from "@common/helpers/sheet";
import { NATIVE_INVOICE_PAGE_SIZE_PX } from "@common/helpers/nativeInvoicePagination";
import { styleWithVars } from "@lib/cssVars";
import frameStyles from "@components/invoices/invoicePaperFrame.module.scss";
import { useInvoicePreviewPaperScale } from "@components/invoices/NativeInvoiceDrawer/useInvoicePreviewPaperScale";
import { LayoutValues } from "@common/schemas/domains/reportSettings/layout-setting.schema";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import {
    buildPreviewVariableContext,
    getPreviewAmountHeader,
} from "./reportPreviewData";
import { useWatermarkScale } from "./useWatermarkScale";
import { getVisiblePreviewLogos } from "./previewLogos";
import { getPreviewDataset } from "./datasets";
import { StatementTable } from "./StatementTable";
import { TrialBalanceTable } from "./TrialBalanceTable";
import { LedgerTable } from "./LedgerTable";
import { SchedulesTable } from "./SchedulesTable";
import { ArAgingSummaryTable } from "./ArAgingSummaryTable";
import { ArAgingDetailTable } from "./ArAgingDetailTable";
import styles from "./ReportPreview.module.scss";

/** A "Header and footer" field the layout editor can focus to spotlight its rendered element. */
export type PreviewHighlightTarget =
    | "header"
    | "title"
    | "subtitle"
    | "subtitle2"
    | "footer"
    | "memo";

interface Props {
    numberFormat: NumberFormatValues;
    reportType: AccountingReportType;
    layout: LayoutValues;
    highlight?: PreviewHighlightTarget | null;
    entityName: string | null;
    firmName: string | null;
    firmLogoUrl: string | null;
    entityLogoUrl: string | null;
}

export const ReportPreview = ({
    numberFormat,
    reportType,
    layout,
    highlight = null,
    entityName,
    firmName,
    firmLogoUrl,
    entityLogoUrl,
}: Props) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const pageRef = useRef<HTMLDivElement>(null);
    const { watermarkRef, watermarkScale } = useWatermarkScale({
        pageRef,
        watermarkText: layout.watermarkText,
    });

    const pageScale = useInvoicePreviewPaperScale({
        viewportRef,
        logicalPaper: NATIVE_INVOICE_PAGE_SIZE_PX.a4,
        showPager: false,
    });

    const highlightClassName = (
        target: PreviewHighlightTarget,
        base: string,
    ) => (highlight === target ? `${base} ${styles.highlight}` : base);

    const context = useMemo(
        () => buildPreviewVariableContext({ reportType, entityName, firmName }),
        [reportType, entityName, firmName],
    );

    const text = useMemo(
        () => ({
            header: interpolateReportVariables(layout.header, context),
            title: interpolateReportVariables(layout.title, context),
            subtitle: interpolateReportVariables(layout.subtitle, context),
            subtitle2: interpolateReportVariables(layout.subtitle2, context),
            footer: interpolateReportVariables(layout.footer, context),
            memo: interpolateReportVariables(layout.memo, context),
        }),
        [layout, context],
    );

    const dataset = useMemo(() => {
        const sample = getPreviewDataset(reportType);
        if (sample.shape !== "statement") {
            return sample;
        }
        return {
            ...sample,
            amountHeader:
                context.periodEnd === null
                    ? ""
                    : getPreviewAmountHeader({
                          isPointInTime: context.isPointInTime,
                          periodEnd: context.periodEnd,
                      }),
        };
    }, [reportType, context.isPointInTime, context.periodEnd]);

    const scaleDisclosure = buildScaleDisclosureText(numberFormat);

    const logos = useMemo(
        () =>
            getVisiblePreviewLogos({
                logoOption: layout.logoOption,
                firmLogoUrl,
                entityLogoUrl,
            }),
        [layout.logoOption, firmLogoUrl, entityLogoUrl],
    );

    const renderTable = () => {
        switch (dataset.shape) {
            case "trial-balance":
                return (
                    <TrialBalanceTable
                        dataset={dataset}
                        numberFormat={numberFormat}
                        rowVisibility={layout.rowVisibility}
                    />
                );
            case "ledger":
                return (
                    <LedgerTable
                        dataset={dataset}
                        numberFormat={numberFormat}
                        rowVisibility={layout.rowVisibility}
                    />
                );
            case "schedules":
                return (
                    <SchedulesTable
                        dataset={dataset}
                        numberFormat={numberFormat}
                    />
                );
            case "ar-aging-summary":
                return (
                    <ArAgingSummaryTable
                        dataset={dataset}
                        numberFormat={numberFormat}
                    />
                );
            case "ar-aging-detail":
                return (
                    <ArAgingDetailTable
                        dataset={dataset}
                        numberFormat={numberFormat}
                        asOf={context.periodEnd}
                    />
                );
            case "statement":
                return (
                    <StatementTable
                        dataset={dataset}
                        numberFormat={numberFormat}
                        rowVisibility={layout.rowVisibility}
                    />
                );
        }
    };

    return (
        <div
            ref={viewportRef}
            className={styles.viewport}
            style={{
                aspectRatio:
                    NATIVE_INVOICE_PAGE_SIZE_PX.a4.width /
                    NATIVE_INVOICE_PAGE_SIZE_PX.a4.height,
                maxHeight: NATIVE_INVOICE_PAGE_SIZE_PX.a4.height,
            }}
        >
            <div
                ref={pageRef}
                className={classNames(frameStyles.paperChrome, styles.page)}
                style={{
                    ...NATIVE_INVOICE_PAGE_SIZE_PX.a4,
                    zoom: pageScale,
                }}
            >
                {layout.watermarkText.trim().length > 0 && (
                    <span
                        ref={watermarkRef}
                        className={styles.watermark}
                        style={styleWithVars({
                            "--report-preview-watermark-scale": watermarkScale,
                        })}
                    >
                        {layout.watermarkText}
                    </span>
                )}

                <div className={styles.content}>
                    <div className={styles.reportHeader}>
                        {logos.length > 0 && (
                            <div className={styles.logos}>
                                {logos.map((logo) =>
                                    logo.kind === "image" ? (
                                        <img
                                            key={logo.alt}
                                            src={encodeURI(logo.src)}
                                            alt={logo.alt}
                                            className={styles.logo}
                                        />
                                    ) : (
                                        <span
                                            key={logo.label}
                                            className={styles.logoPlaceholder}
                                        >
                                            {logo.label}
                                        </span>
                                    ),
                                )}
                            </div>
                        )}
                        {text.header.trim().length > 0 && (
                            <span
                                className={highlightClassName(
                                    "header",
                                    styles.headerLine,
                                )}
                            >
                                {text.header}
                            </span>
                        )}
                        {text.title.trim().length > 0 && (
                            <span
                                className={highlightClassName(
                                    "title",
                                    styles.title,
                                )}
                            >
                                {text.title}
                            </span>
                        )}
                        {text.subtitle.trim().length > 0 && (
                            <span
                                className={highlightClassName(
                                    "subtitle",
                                    styles.subtitle,
                                )}
                            >
                                {text.subtitle}
                            </span>
                        )}
                        {text.subtitle2.trim().length > 0 && (
                            <span
                                className={highlightClassName(
                                    "subtitle2",
                                    styles.subtitle2,
                                )}
                            >
                                {text.subtitle2}
                            </span>
                        )}
                        {scaleDisclosure.length > 0 && (
                            <span className={styles.scaleDisclosure}>
                                {scaleDisclosure}
                            </span>
                        )}
                    </div>

                    {renderTable()}

                    {shouldShowRoundingDisclosure(numberFormat) && (
                        <p className={styles.roundingFootnote}>
                            {ROUNDING_FOOTNOTE_TEXT}
                        </p>
                    )}

                    {text.memo.trim().length > 0 && (
                        <p className={highlightClassName("memo", styles.memo)}>
                            {text.memo}
                        </p>
                    )}
                </div>

                {text.footer.trim().length > 0 && (
                    <div
                        className={highlightClassName(
                            "footer",
                            styles.reportFooter,
                        )}
                    >
                        <span>{text.footer}</span>
                        <span className={styles.amountCell}>Page 1 of 1</span>
                    </div>
                )}
            </div>
        </div>
    );
};
